'use client';

/**
 * ShippingInfoBox Component
 *
 * Displays region-specific shipping information in a grey box.
 * Shows shipping policy, delivery times, and signature requirements.
 *
 * Used in:
 * - Shopping bag sidebar
 * - Cart drawer
 *
 * Content varies by channel/region.
 */

import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { getCountryCodeFromChannel } from '@/lib/const';
import styles from './ShippingInfoBox.module.scss';

export interface ShippingInfoBoxProps {
  /** Channel slug for region-specific content (e.g., 'us-usd', 'de-eur') */
  channelSlug?: string;
  /** Additional class name */
  className?: string;
}

/** Truck icon for shipping */
const TruckIcon = ({ size = 18 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

/**
 * Get shipping info translation key based on country
 * Returns null if no specific shipping info should be shown
 *
 * Note: Currently only shows for US to match the old Breitling behavior.
 * Other countries can be added by adding translation keys and updating this function.
 */
function getShippingInfoKey(channelSlug?: string): string | null {
  const countryCode = getCountryCodeFromChannel(channelSlug);

  // Only show shipping info for US (matching old Breitling behavior)
  if (countryCode === 'US') {
    return 'shippingInfoUS';
  }

  // Other countries can be enabled by adding translation keys:
  // case 'GB': return 'shippingInfoGB';
  // case 'DE': return 'shippingInfoDE';
  // etc.

  return null;
}

/**
 * ShippingInfoBox - Regional shipping information
 */
export function ShippingInfoBox({ channelSlug, className }: ShippingInfoBoxProps) {
  const t = useTranslations('checkout');

  const shippingInfoKey = getShippingInfoKey(channelSlug);

  // Don't render if no channel or no shipping info for this region
  if (!shippingInfoKey) {
    return null;
  }

  const shippingText = t(`cart.${shippingInfoKey}`);

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.icon}>
        <TruckIcon size={18} />
      </div>
      <p className={styles.text}>{shippingText}</p>
    </div>
  );
}
